<template>
    <div>
        <div v-if="$page.props.auth.user" class="w-full mb-8">
            <VerticalSlideTransition
                :show="comments.length > 0"
                wrapperClass="w-full"
                class="min-w-0 w-full"
            >
                <div class="rounded-r-lg overflow-hidden">
                    <div
                        :class="[
                            'w-full min-h-[400px] max-h-[800px] overflow-y-scroll border-b scroll-smooth pr-3',
                            `scrollbar-${modelClass}`,
                        ]"
                        ref="commentsContainer"
                    >
                        <TransitionGroup name="list-transition">
                            <Comment
                                v-for="comment in comments"
                                @removed="commentRemoved"
                                :ref="'comment-' + comment.id"
                                :key="comment.id"
                                :initialComment="comment"
                                :availableEmotions="availableEmotions"
                                :modelId="modelId"
                                :modelClass="modelClass"
                                :highlightCommentId="highlightCommentId"
                            />
                        </TransitionGroup>
                    </div>
                </div>
            </VerticalSlideTransition>
            <VerticalSlideTransition :show="loading">
                <div class="flex justify-center items-center">
                    <CommentLoadIcon class="text-text-secondary" />
                </div>
            </VerticalSlideTransition>
        </div>
        <div class="relative" ref="commentinputContainer">
            <Transition name="fade" mode="out-in">
                <WhisperText
                    v-if="usersTypingText"
                    :whisperText="usersTypingText"
                    class="absolute bottom-0 translate-y-full"
                />
            </Transition>

            <TextAreaInput
                @focus="textareaFocused = true"
                @blur="onTextareaBlur"
                @keydown.enter="handleEnterPress"
                @keydown.escape="() => $refs.textarea.blurMethod()"
                @keyup="
                    whisper(
                        $page.props.auth.user.id,
                        this.$page.props.auth.user.name,
                        { commentId: null }
                    )
                "
                ref="textarea"
                v-model="comment"
                :placeholder="$lang.get('Write your comment...')"
                :disabled="savingComment"
                :autoAdjustHeight="true"
            />
            <Transition name="fade">
                <PrimaryButton
                    v-if="textareaFocused"
                    :disabled="!canSaveComment"
                    size="xs"
                    class="absolute right-1.5 bottom-5 rounded-full p-2"
                    @click="addComment"
                >
                    <PhPaperPlaneTilt class="size-6" />
                </PrimaryButton>
            </Transition>
        </div>
        <InputError :message="errors?.comment" />
    </div>
</template>

<script>
import { PhPaperPlaneTilt } from '@phosphor-icons/vue'

import { useInfiniteScroll } from '@vueuse/core'
import PrimaryButton from '@/Components/Elements/PrimaryButton.vue'
import VerticalSlideTransition from '@/Components/Transitions/VerticalSlideTransition.vue'
import CommentLoadIcon from '@/Components/ModelComponents/Interactions/CommentLoadIcon.vue'
import Comment from '@/Components/ModelComponents/Interactions/Comment.vue'
import TextAreaInput from '@/Components/FormElements/TextAreaInput.vue'
import { minimumLoadingTime } from '@/Tools/utilities'
import broadcastMixin from '@/Mixins/broadcastMixin.js'
import InputError from '@/Components/FormElements/InputError.vue'
import WhisperText from '@/Components/Chat/WhisperText.vue'
export default {
    name: 'Comments',

    components: {
        PhPaperPlaneTilt,
        PrimaryButton,
        VerticalSlideTransition,
        CommentLoadIcon,
        Comment,
        TextAreaInput,
        InputError,
        WhisperText,
    },

    mixins: [broadcastMixin],

    props: {
        modelId: {
            type: Number,
            required: true,
        },
        modelClass: {
            type: String,
            required: true,
        },
        availableEmotions: {
            type: Array,
            default: () => [],
        },
    },

    data() {
        return {
            echoChannel: null,
            highlightCommentId: null,
            comments: [],
            comment: '',
            textareaFocused: false,
            loading: false,
            savingComment: false,
            hasMoreComments: false,
            page: 0,
            infiniteScrollStop: null,
            errors: null,
        }
    },

    mounted() {
        this.initEcho()
        this.fetchComments()
        this.focusComments()
    },

    beforeUnmount() {
        this.destroyEcho()
    },

    computed: {
        canSaveComment() {
            return this.comment.length >= 2 && !this.savingComment
        },

        usersTypingText() {
            if (this.typingUsers.length == 1) {
                return this.$lang.get(':name is writing a comment', {
                    name: this.typingUsers[0].name,
                })
            } else if (this.typingUsers.length > 0) {
                return this.$lang.get(
                    ':nbrTyping others are writing comments',
                    {
                        nbrTyping: this.typingUsers.length,
                    }
                )
            }
            return null
        },

        /**
         * Will check the URL for a comment_id, if found we will scroll to it
         * using the scrollToComment method
         */
        commentLink() {
            const url = new URL(window.location.href)
            return url.searchParams.get('comment_id')
        },
    },

    methods: {
        handleEnterPress(event) {
            if (!event.shiftKey) {
                this.addComment()
            }
        },
        addComment() {
            this.savingComment = true
            this.errors = null

            axios
                .post(this.route('api.interaction.comment.create'), {
                    model_class: this.modelClass,
                    model_id: this.modelId,
                    comment: this.comment,
                })
                .then((response) => {
                    this.comment = ''
                    this.comments.unshift(response.data)
                    this.$nextTick(() => {
                        this.scrollToComment(response.data.id)
                    })
                })
                .catch(({ response }) => {
                    this.errors = response.data.errors
                })
                .finally(() => {
                    this.editing = false
                    this.savingComment = false
                })
        },

        commentRemoved(id) {
            this.comments = this.comments.filter((comment) => comment.id !== id)
        },

        async fetchComments(initialLoad = true) {
            if (!this.$page.props.auth.user) return
            if (this.loading) return

            this.loading = true
            let limit = null

            if (initialLoad && this.commentLink) {
                let { position } = await this.getPageInfoOfComment(
                    this.commentLink
                )
                limit = position ? position : null
            }

            const handleResponse = ({ data }) => {
                if (data.data.length > 0) {
                    if (initialLoad) {
                        this.comments = data.data
                    } else {
                        this.comments.push(...data.data)
                    }
                    this.hasMoreComments = data.next_page_url !== null
                } else {
                    this.hasMoreComments = false
                }

                this.loading = false

                if (initialLoad) {
                    this.$nextTick(() => {
                        this.initInfiniteScroll()
                        // Scroll to and highlight the comment present in the url
                        this.scrollToComment(this.commentLink, true)
                    })
                }
            }

            // Only wrap in minimumLoadingTime for initial load, this is to make a smooth transition
            const requestHandler = initialLoad
                ? minimumLoadingTime(handleResponse, 500)
                : handleResponse

            let params = {
                modelId: this.modelId,
                modelClass: this.modelClass,
                page: this.page,
            }

            // we use limit to scroll to a specific comment
            // default is null, because we want the backend to decide the limit
            if (limit) {
                params.limit = limit
            }

            axios
                .get(route('api.interaction.comments', params))
                .then((response) => {
                    requestHandler(response)
                    this.page = this.page + 1
                })
        },

        initInfiniteScroll() {
            const { stop } = useInfiniteScroll(
                this.$refs.commentsContainer,
                () => {
                    this.fetchComments(false)
                },
                {
                    distance: 50,
                    canLoadMore: () => this.hasMoreComments,
                    interval: 300,
                }
            )
            this.infiniteScrollStop = stop
        },

        fetchSingleComment(commentId) {
            axios
                .get(
                    route('api.interaction.comment.show', {
                        interaction: commentId,
                    })
                )
                .then((response) => {
                    this.comments.unshift(response.data)
                })
        },

        getPageInfoOfComment(commentId) {
            return axios
                .get(
                    route('api.interaction.comment.paginationinfo', {
                        id: commentId,
                    })
                )
                .then((response) => {
                    return response.data
                })
                .catch(() => {
                    this.$bus.emit(
                        'flash-error',
                        this.$lang.get('The comment has been removed')
                    )
                    return { position: null }
                })
        },

        onTextareaBlur() {
            setTimeout(() => {
                if (!this.savingComment && this.comment.length == 0) {
                    this.textareaFocused = false
                }
            }, 200)
        },

        initEcho() {
            if (this.$page.props.auth.user) {
                this.echoChannel = window.Echo.private(
                    `${this.modelClass}-${this.modelId}-interactions`
                )

                this.echoChannel.listenForWhisper('typing', (event) => {
                    if (event.commentId == null) {
                        this.handleWhisper(event)
                    }
                })

                this.echoChannel.listen('CommentCreatedEvent', (event) => {
                    if (event.parentId == null) {
                        this.fetchSingleComment(event.commentId)
                        this.checkIfTypingUserPosted(event.user.id)
                    }
                })

                this.echoChannel.listen('CommentDeletedEvent', (event) => {
                    if (event.parentId == null) {
                        this.commentRemoved(event.commentId)
                    }
                })
            }
        },

        destroyEcho() {
            if (this.$page.props.auth.user) {
                window.Echo.leaveChannel(
                    `${this.modelClass}-${this.modelId}-interactions`
                )
                this.echoChannel = null
            }
        },

        /**
         * Will try and scroll to the comment with the given id
         * The comment might be a child, and would then be loaded slightly after the top level comments
         * This happens since its the responsibility of the parent comment to render its children
         * We try three times with a minor delay to ensure that the comment has been rendered
         */
        scrollToComment(id = null, highlight = false) {
            if (!id || !this.$refs.commentsContainer) {
                return
            }

            if (highlight) {
                this.highlightCommentId = parseInt(id)
            }

            let tryToScroll = (counter = 0) => {
                let element = this.$refs.commentsContainer.querySelector(
                    `#comment-${id}`
                )

                if (element) {
                    setTimeout(() => {
                        element.scrollIntoView({
                            behavior: 'smooth',
                            block: 'center',
                        })
                    }, 500)

                    // If the comment is not found, we wait 200ms and try again (3 times total)
                } else if (counter < 3) {
                    setTimeout(() => {
                        return tryToScroll(counter + 1)
                    }, 200)
                }
            }

            tryToScroll()
        },

        /**
         * Will check if the url has comments = true, if so we will scroll to the comments and set focus on the textarea
         */
        focusComments() {
            if (
                !this.highlightCommentId &&
                this.$page.url.includes('comments=true')
            ) {
                setTimeout(() => {
                    this.$refs.commentinputContainer.scrollIntoView({
                        behavior: 'smooth',
                        block: 'center',
                    })
                    setTimeout(() => {
                        this.$refs.textarea.focusMethod()
                    }, 500)
                }, 500)
            }
        },
    },
}
</script>
